<script setup>
// Consistent public-facing message for entities that have been deleted (archived) by their
// organiser. The API now returns archived clubs/leagues/ladders/tournaments with archivedAt
// set instead of a 404 - shown in place of the content so deleted data isn't exposed.
const props = defineProps({
  entity: {
    type: String,
    default: 'page'
  },
  // Display name of the deleted entity, e.g. "Monday Night Ladder"
  name: {
    type: String,
    default: null
  }
});

const title = computed(() =>
    props.name ? `${props.name} has been deleted` : `This ${props.entity} has been deleted`);

// Direct/shared links have no in-app history to return to - offer Home instead.
// vue-router records the previous route in history.state.back (null on a fresh entry);
// checked in onMounted because window doesn't exist during SSR.
const router = useRouter();
const hasHistory = ref(false);
onMounted(() => {
  hasHistory.value = !!window.history.state?.back;
});

const goBack = () => {
  if (hasHistory.value) {
    router.back();
  } else {
    router.push('/');
  }
};
</script>

<template>
  <div class="tl-card an-notice">
    <div class="an-icon">
      <v-icon size="26" color="error">mdi-delete-outline</v-icon>
    </div>
    <h2 class="an-title">{{ title }}</h2>
    <p class="an-body">
      This {{ entity }} was deleted by its organiser, so it's no longer available to view.
    </p>
    <v-btn
        class="mt-4"
        color="primary"
        variant="flat"
        size="small"
        @click="goBack"
    >
      <v-icon size="16" class="mr-1" :icon="hasHistory ? 'mdi-arrow-left' : 'mdi-home'"></v-icon>
      {{ hasHistory ? 'Go back' : 'Go home' }}
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
.an-notice {
  text-align: center;
  padding: 40px 24px;
}

.an-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgb(var(--v-theme-brandTint));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.an-title {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.4px;
}

.an-body {
  font-size: 13px;
  color: rgb(var(--v-theme-charcoal));
  margin: 8px auto 0;
  line-height: 1.7;
  max-width: 380px;
}
</style>
